import { useState } from 'react';
import { CheckCircle, BookOpen, Award, Users, Clock, ThumbsUp, BookMarked, Mail } from 'lucide-react';
import HighlightText from '../components/core/HomePage/HighlightText';
import Footer from '../components/common/Footer';

export default function WritingService() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    alert('Form submitted successfully!');
  };

  return (
    <div className="min-h-screen bg-richblue-5 text-richblue-700">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-richblue-200 to-richblue-100 text-richblue-700 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Professional Thesis Writing Service</h1>
            <p className="text-xl mb-8 text-richblue-500">Expert assistance for your academic success. Get help from PhD-qualified writers in all disciplines.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="bg-richblue-400 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-richblue-500 transition duration-300">
                Order Now
              </button>
              <button className="bg-transparent border-2 border-richblue-400 text-richblue-600 font-bold py-3 px-6 rounded-lg hover:bg-richblue-400 hover:text-white transition duration-300">
                Free Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-richblue-5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Our Thesis Writing <HighlightText text={"Service"} /></h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition duration-300">
              <div className="flex items-center mb-4">
                <CheckCircle className="text-richblue-400 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-richblue-600">100% Original Content</h3>
              </div>
              <p className="text-richblue-500">Every thesis is written from scratch, tailored to your specific requirements and thoroughly checked for plagiarism.</p>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition duration-300">
              <div className="flex items-center mb-4">
                <BookOpen className="text-richblue-400 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-richblue-600">PhD-Qualified Writers</h3>
              </div>
              <p className="text-richblue-500">Our team consists of subject specialists with advanced degrees from prestigious universities worldwide.</p>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition duration-300">
              <div className="flex items-center mb-4">
                <Award className="text-richblue-400 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-richblue-600">Quality Guarantee</h3>
              </div>
              <p className="text-richblue-500">We promise the highest academic standards with thorough research and impeccable writing quality.</p>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition duration-300">
              <div className="flex items-center mb-4">
                <Users className="text-richblue-400 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-richblue-600">24/7 Support</h3>
              </div>
              <p className="text-richblue-500">Our customer support team is available around the clock to address your concerns and answer questions.</p>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition duration-300">
              <div className="flex items-center mb-4">
                <Clock className="text-richblue-400 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-richblue-600">On-time Delivery</h3>
              </div>
              <p className="text-richblue-500">We respect deadlines and ensure your thesis is delivered on or before the agreed date.</p>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition duration-300">
              <div className="flex items-center mb-4">
                <ThumbsUp className="text-richblue-400 mr-3" size={24} />
                <h3 className="text-xl font-semibold text-richblue-600">Free Revisions</h3>
              </div>
              <p className="text-richblue-500">We offer unlimited revisions within 30 days to ensure you're completely satisfied with your thesis.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-richblue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Our Thesis Writing <HighlightText text={"Services"} /></h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-richblue-5 p-6 rounded-lg border border-richblue-300">
              <h3 className="text-2xl font-semibold text-richblue-600 mb-4">Masters Thesis</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="text-richblue-400 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-richblue-500">In-depth research and literature review</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-richblue-400 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-richblue-500">Comprehensive methodology development</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-richblue-400 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-richblue-500">Data analysis and interpretation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-richblue-400 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-richblue-500">Professional editing and formatting</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg border border-richblue-300">
              <h3 className="text-2xl font-semibold text-richblue-600 mb-4">PhD Dissertation</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="text-richblue-400 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-richblue-500">Advanced theoretical framework development</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-richblue-400 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-richblue-500">Original research contribution</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-richblue-400 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-richblue-500">Complex statistical analysis</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-richblue-400 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-richblue-500">Publication-ready formatting</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="text-center p-5">
              <div className="bg-richblue-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <BookMarked className="text-richblue-400" size={24} />
              </div>
              <h4 className="text-lg font-semibold mb-2 text-richblue-600">Thesis Proposal</h4>
              <p className="text-richblue-500">Get help crafting a compelling proposal that will impress your committee.</p>
            </div>
            
            <div className="text-center p-5">
              <div className="bg-richblue-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <BookMarked className="text-richblue-400" size={24} />
              </div>
              <h4 className="text-lg font-semibold mb-2 text-richblue-600">Literature Review</h4>
              <p className="text-richblue-500">Comprehensive analysis of relevant academic sources for your research.</p>
            </div>
            
            <div className="text-center p-5">
              <div className="bg-richblue-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <BookMarked className="text-richblue-400" size={24} />
              </div>
              <h4 className="text-lg font-semibold mb-2 text-richblue-600">Editing & Proofreading</h4>
              <p className="text-richblue-500">Polish your existing thesis to perfection with our expert editors.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-richblue-5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Our Writing <HighlightText text={"Process"} /></h2>
          
          <div className="flex flex-col md:flex-row justify-between items-start">
            <div className="flex-1 relative pb-12 md:pb-0">
              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 relative z-10 md:mr-4">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-10 h-10 flex items-center justify-center font-bold mb-4">1</div>
                <h3 className="text-xl font-semibold mb-3 text-richblue-600">Order Placement</h3>
                <p className="text-richblue-500">Submit your requirements, including topic, length, deadline, and any specific instructions.</p>
              </div>
              <div className="hidden md:block absolute top-1/2 left-full w-full h-1 bg-richblue-200 -translate-y-1/2 z-0"></div>
            </div>
            
            <div className="flex-1 relative pb-12 md:pb-0">
              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 relative z-10 md:mx-4">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-10 h-10 flex items-center justify-center font-bold mb-4">2</div>
                <h3 className="text-xl font-semibold mb-3 text-richblue-600">Writer Assignment</h3>
                <p className="text-richblue-500">We match your project with a qualified writer who specializes in your field of study.</p>
              </div>
              <div className="hidden md:block absolute top-1/2 left-full w-full h-1 bg-richblue-200 -translate-y-1/2 z-0"></div>
            </div>
            
            <div className="flex-1 relative pb-12 md:pb-0">
              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 relative z-10 md:mx-4">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-10 h-10 flex items-center justify-center font-bold mb-4">3</div>
                <h3 className="text-xl font-semibold mb-3 text-richblue-600">Research & Writing</h3>
                <p className="text-richblue-500">Your writer conducts thorough research and crafts your thesis according to your specifications.</p>
              </div>
              <div className="hidden md:block absolute top-1/2 left-full w-full h-1 bg-richblue-200 -translate-y-1/2 z-0"></div>
            </div>
            
            <div className="flex-1 relative">
              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 relative z-10 md:ml-4">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-10 h-10 flex items-center justify-center font-bold mb-4">4</div>
                <h3 className="text-xl font-semibold mb-3 text-richblue-600">Quality Check & Delivery</h3>
                <p className="text-richblue-500">Your thesis undergoes rigorous quality checks before being delivered to you for review.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-richblue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">What Our Clients <HighlightText text={"Say"} /></h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-richblue-100 rounded-full flex items-center justify-center text-richblue-600 font-bold text-xl mr-4">S</div>
                <div>
                  <h4 className="font-semibold text-richblue-600">Sarah K.</h4>
                  <p className="text-sm text-richblue-500">PhD Candidate, Psychology</p>
                </div>
              </div>
              <p className="text-richblue-500 italic">"The dissertation help I received was exceptional. My writer understood the complex theoretical framework I was working with and delivered a well-researched paper that impressed my committee."</p>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-richblue-100 rounded-full flex items-center justify-center text-richblue-600 font-bold text-xl mr-4">M</div>
                <div>
                  <h4 className="font-semibold text-richblue-600">Michael T.</h4>
                  <p className="text-sm text-richblue-500">Masters Student, Business</p>
                </div>
              </div>
              <p className="text-richblue-500 italic">"I was struggling with my MBA thesis until I found this service. The writer provided valuable insights and crafted a compelling analysis that earned me a distinction."</p>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-richblue-100 rounded-full flex items-center justify-center text-richblue-600 font-bold text-xl mr-4">J</div>
                <div>
                  <h4 className="font-semibold text-richblue-600">Jennifer L.</h4>
                  <p className="text-sm text-richblue-500">PhD Student, Engineering</p>
                </div>
              </div>
              <p className="text-richblue-500 italic">"The technical expertise of my writer was impressive. They handled complex engineering concepts with ease and produced a dissertation that exceeded my expectations."</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA & Contact Form */}
      <section className="py-16 bg-gradient-to-r from-richblue-200 to-richblue-100">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-richblue-600">Ready to Excel in Your Academic Journey?</h2>
              <p className="text-lg mb-8 text-richblue-500">Contact us today for a free consultation and let our expert thesis writers help you achieve academic excellence.</p>
              
              <div className="bg-richblue-5 p-6 rounded-lg border border-richblue-300">
                <div className="flex items-center mb-4">
                  <Mail className="mr-3 text-richblue-400" size={24} />
                  <span className="text-richblue-500">support@thesiswriting.com</span>
                </div>
                <div className="flex items-center">
                  <Clock className="mr-3 text-richblue-400" size={24} />
                  <span className="text-richblue-500">24/7 Customer Support</span>
                </div>
              </div>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
              <h3 className="text-2xl font-semibold mb-6 text-richblue-600">Get Your Free Consultation</h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-richblue-500 font-medium mb-2">Full Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    className="w-full px-4 py-2 bg-richblue-100 border border-richblue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-richblue-400 text-richblue-900" 
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="email" className="block text-richblue-500 font-medium mb-2">Email Address</label>
                  <input 
                    type="email" 
                    id="email" 
                    className="w-full px-4 py-2 bg-richblue-100 border border-richblue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-richblue-400 text-richblue-900" 
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="topic" className="block text-richblue-500 font-medium mb-2">Thesis Topic</label>
                  <input 
                    type="text" 
                    id="topic" 
                    className="w-full px-4 py-2 bg-richblue-100 border border-richblue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-richblue-400 text-richblue-900" 
                    placeholder="Enter your thesis topic"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                  />
                </div>
                
                <div className="mb-6">
                  <label htmlFor="message" className="block text-richblue-500 font-medium mb-2">Message</label>
                  <textarea 
                    id="message" 
                    rows="4" 
                    className="w-full px-4 py-2 bg-richblue-100 border border-richblue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-richblue-400 text-richblue-900" 
                    placeholder="Tell us about your requirements"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  ></textarea>
                </div>
                
                <button type="submit" className="w-full bg-richblue-400 text-white font-bold py-3 px-6 rounded-lg hover:bg-richblue-500 transition duration-300">
                  Submit Request
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}